import { AVATAR_CIRCLE_INSET, OUTPUT_WIDTH } from '../constants';
import { BackgroundSelection, PreparedImage } from './convertImage';

export const renderCanvas = async ({
    imageData,
    containerWidth,
    containerHeight,
    fitScale,
    zoom,
    rotation,
    offsetX,
    offsetY,
    aspectRatio,
    avatar = false,
    backgroundSelection,
    outputWidth = OUTPUT_WIDTH,
    outputHeight = avatar ? OUTPUT_WIDTH : Math.round(OUTPUT_WIDTH / aspectRatio),
}: {
    imageData: PreparedImage;
    containerWidth: number;
    containerHeight: number;
    fitScale: number;
    zoom: number;
    rotation: number;
    offsetX: number;
    offsetY: number;
    aspectRatio: number;
    avatar?: boolean;
    backgroundSelection: BackgroundSelection;
    outputWidth?: number;
    outputHeight?: number;
}): Promise<string | null> => {
    if (
        !containerWidth ||
        !containerHeight ||
        !imageData.width ||
        !imageData.height
    ) {
        return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return null;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageData.src;
    await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Image failed to load'));
    });

    const avatarViewportSize = containerHeight - AVATAR_CIRCLE_INSET * 2;
    const outputAvatarInset = 0;
    const outputViewportWidth = avatar
        ? outputHeight - outputAvatarInset * 2
        : outputWidth;
    const viewportWidth = avatar ? avatarViewportSize : containerWidth;
    const scaleToOutput = outputViewportWidth / viewportWidth;
    if (!fitScale) {
        return null;
    }
    const drawScale = fitScale * zoom * scaleToOutput;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    if (backgroundSelection.type === 'color') {
        ctx.fillStyle = backgroundSelection.color;
        ctx.fillRect(0, 0, outputWidth, outputHeight);
    } else {
        const coverScale = Math.max(
            outputWidth / img.naturalWidth,
            outputHeight / img.naturalHeight,
        );
        const coverWidth = img.naturalWidth * coverScale;
        const coverHeight = img.naturalHeight * coverScale;
        ctx.save();
        ctx.filter = 'blur(22px)';
        ctx.drawImage(
            img,
            (outputWidth - coverWidth) / 2,
            (outputHeight - coverHeight) / 2,
            coverWidth,
            coverHeight,
        );
        ctx.restore();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
        ctx.fillRect(0, 0, outputWidth, outputHeight);
    }

    ctx.save();
    ctx.translate(
        outputWidth / 2 + offsetX * scaleToOutput,
        outputHeight / 2 + offsetY * scaleToOutput,
    );
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(drawScale, drawScale);
    ctx.drawImage(img, -imageData.width / 2, -imageData.height / 2);
    ctx.restore();

    if (avatar) {
        const maskedCanvas = document.createElement('canvas');
        maskedCanvas.width = canvas.width;
        maskedCanvas.height = canvas.height;

        const maskedCtx = maskedCanvas.getContext('2d');
        if (!maskedCtx) {
            return null;
        }

        maskedCtx.drawImage(canvas, 0, 0);
        maskedCtx.globalCompositeOperation = 'destination-in';
        maskedCtx.beginPath();
        maskedCtx.arc(
            outputWidth / 2,
            outputHeight / 2,
            outputViewportWidth / 2,
            0,
            Math.PI * 2,
        );
        maskedCtx.closePath();
        maskedCtx.fill();

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(maskedCanvas, 0, 0);
    }

    return canvas.toDataURL('image/png');
};
